import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Button, Flex, Box } from '../Rebass';
import { ButtonStandardProps, ButtonVariant, ISizeMap } from './types';
import Text from '../Text';
import theme from '../../theme';
import { resolveColor } from '../../utils/helpers';
import useClickOutside from '../../hooks/useClickOutside';

interface IStyledIcon {
  color: string;
  order: number;
}

interface ButtonColors {
  bg: string;
  color: string;
  border?: string;
  hoverBg: string;
  hoverText: string;
}

const StyledButton = styled(Button)<
  ButtonColors & { variant: ButtonVariant; onClick: () => void }
>(({ hoverBg, hoverText, border }) => {
  return `
  cursor: pointer;
  border-radius: 5px;
  transition: background ${theme.speed.default} ease;
  ${border ? `border: ${border}` : ''};

  :hover {
    background-color: ${hoverBg};

    & .button-text, .icon {
      color: ${hoverText};
    }
  }
`;
});

const StyledText = styled(Text)`
  transition: color ${theme.speed.default} ease;
  white-space: nowrap;
`;

const StyledIcon = styled.i<IStyledIcon>(
  ({ color, order }) => `
  order: ${order};
  color: ${color};
  transition: color ${theme.speed.default} ease;
  margin: ${
    order === 0 ? `0 ${theme.space[1]}px 0 0` : `0 0 0 ${theme.space[1]}px`
  };
  font-size: ${theme.fontSizes.extraSmall} !important;
  display: flex !important;
  align-items: center;
`,
);

const sizeMap: ISizeMap = {
  default: {
    padding: {
      px: [2, 4],
      py: ['11px'],
    },
    text: 'small',
  },
  small: {
    padding: {
      px: [2],
      py: [1],
    },
    text: 'tiny',
  },
};

function getButtonColors(color: string, variant: ButtonVariant): ButtonColors {
  switch (variant) {
    case 'primary':
      return {
        bg: color,
        border: `1px solid ${color}`,
        // TODO: this is an edge case with the white button, need a more elegant way to solve
        color:
          color === theme.colors.textWhite
            ? theme.colors.textDark
            : theme.colors.textWhite,
        hoverBg: theme.colors.transparent,
        hoverText: color,
      };
    case 'outline':
      return {
        bg: theme.colors.transparent,
        border: `1px solid ${color}`,
        color,
        hoverBg: color,
        // TODO: this is an edge case with the white button, need a more elegant way to solve
        hoverText:
          color === theme.colors.baseWhite
            ? theme.colors.textDark
            : theme.colors.textWhite,
      };
    case 'simple':
      return {
        bg: theme.colors.transparent,
        color,
        hoverBg: color,
        hoverText: theme.colors.textWhite,
      };
  }
}

const ButtonStandard = ({
  size = 'default',
  children,
  variant = 'primary',
  icon,
  iconPosition,
  onClick,
  renderPopover,
  popoverPosition = 'bottom',
  popoverPositionHorizontal = 'left',
  colorScheme = 'brandBrown',
  wrapperSx = {},
  ...rest
}: ButtonStandardProps) => {
  const [isClicked, setClicked] = useState(false);
  const sizeProps = sizeMap[size];

  const colors = getButtonColors(resolveColor(colorScheme), variant);

  const [iconOrder, textOrder] = iconPosition === 'left' ? [0, 1] : [1, 0];

  const wrapperRef = useClickOutside(() => setClicked(false));

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      {/*// @ts-ignore */}
      <StyledButton
        variant={variant}
        onClick={() => {
          (onClick as () => void)?.();
          setClicked(!isClicked);
        }}
        {...sizeProps.padding}
        {...colors}
        {...rest}
      >
        <Flex sx={wrapperSx}>
          <Box sx={{ order: textOrder }}>
            <StyledText
              className="button-text"
              size={sizeProps.text}
              color={colors.color}
            >
              {children}
            </StyledText>
          </Box>
          {icon && (
            <StyledIcon
              data-testid="button-icon"
              order={iconOrder}
              color={colors.color}
              className={`icon icon-${icon}`}
            />
          )}
        </Flex>
      </StyledButton>
      {renderPopover && (
        <Box
          sx={{
            display: isClicked ? 'block' : 'none',
            position: 'absolute',
            ...(popoverPositionHorizontal === 'left'
              ? { left: 0 }
              : { right: 0 }),
            ...(popoverPosition === 'bottom'
              ? { top: '100%', paddingTop: '5px' }
              : { bottom: '100%', paddingBottom: '5px' }),
          }}
        >
          {renderPopover(colorScheme)}
        </Box>
      )}
    </Box>
  );
};

export default ButtonStandard;
